import { useEffect, useRef, type ReactNode } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { cx } from './cx'

export interface Column<T> {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  sortable?: boolean
  align?: 'left' | 'right'
  width?: string
  // Visually hidden header text (e.g. an actions column)
  srOnlyHeader?: boolean
}

export interface SortState {
  key: string
  direction: 'asc' | 'desc'
}

export interface TableProps<T> {
  columns: Array<Column<T>>
  rows: T[]
  rowKey: (row: T) => string
  caption: string
  // Selection is controlled; omit to render without checkboxes
  selected?: Set<string>
  onSelectionChange?: (selected: Set<string>) => void
  rowLabel?: (row: T) => string
  // Sorting is controlled (server- or client-side, the caller decides)
  sort?: SortState
  onSortChange?: (sort: SortState) => void
  onRowClick?: (row: T) => void
  loading?: boolean
  empty?: ReactNode
  footer?: ReactNode
  toolbar?: ReactNode
  minWidth?: number
}

function SelectAll({ checked, indeterminate, onChange }: { checked: boolean; indeterminate: boolean; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return <input ref={ref} type="checkbox" aria-label="Select all rows on this page" checked={checked} onChange={onChange} className="h-4 w-4 accent-brand" />
}

export function Table<T>({
  columns, rows, rowKey, caption, selected, onSelectionChange, rowLabel, sort, onSortChange,
  onRowClick, loading = false, empty, footer, toolbar, minWidth = 720,
}: TableProps<T>) {
  const selectable = !!selected && !!onSelectionChange
  const pageKeys = rows.map(rowKey)
  const selectedOnPage = pageKeys.filter(k => selected?.has(k)).length
  const allSelected = pageKeys.length > 0 && selectedOnPage === pageKeys.length

  function toggleAll() {
    if (!selected || !onSelectionChange) return
    const next = new Set(selected)
    pageKeys.forEach(k => (allSelected ? next.delete(k) : next.add(k)))
    onSelectionChange(next)
  }

  function toggleRow(key: string) {
    if (!selected || !onSelectionChange) return
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onSelectionChange(next)
  }

  function headerSort(column: Column<T>) {
    if (!column.sortable || !onSortChange) return
    const direction = sort?.key === column.key && sort.direction === 'desc' ? 'asc' : 'desc'
    onSortChange({ key: column.key, direction })
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      {toolbar}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px] text-ink" style={{ minWidth }} aria-busy={loading || undefined}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="text-left text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              {selectable && (
                <th scope="col" className="w-8 border-b border-line px-4 py-2.5">
                  <SelectAll checked={allSelected} indeterminate={selectedOnPage > 0 && !allSelected} onChange={toggleAll} />
                </th>
              )}
              {columns.map(column => {
                const active = sort?.key === column.key
                return (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cx('border-b border-line px-2 py-2.5 first:pl-4 last:pr-4', column.align === 'right' && 'text-right')}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => headerSort(column)}
                        className="inline-flex items-center gap-1 uppercase hover:text-ink focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus rounded"
                      >
                        {column.header}
                        {active && (sort!.direction === 'asc'
                          ? <ArrowUp className="h-3 w-3" aria-hidden="true" />
                          : <ArrowDown className="h-3 w-3" aria-hidden="true" />)}
                      </button>
                    ) : column.srOnlyHeader ? (
                      <span className="sr-only">{column.header}</span>
                    ) : column.header}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const key = rowKey(row)
              const isSelected = selected?.has(key) ?? false
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cx(isSelected ? 'bg-brand-tint/60' : 'bg-surface', onRowClick && 'cursor-pointer hover:bg-surface-raised')}
                >
                  {selectable && (
                    <td className="border-b border-line px-4 py-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${rowLabel ? rowLabel(row) : 'row'}`}
                        checked={isSelected}
                        onChange={() => toggleRow(key)}
                        className="h-4 w-4 accent-brand"
                      />
                    </td>
                  )}
                  {columns.map(column => (
                    <td key={column.key} className={cx('border-b border-line px-2 py-3 align-middle first:pl-4 last:pr-4', column.align === 'right' && 'text-right')}>
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && empty && <div className="p-4">{empty}</div>}
        {loading && rows.length === 0 && (
          <div className="flex justify-center p-8" role="status">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-transparent" aria-hidden="true" />
            <span className="sr-only">Loading</span>
          </div>
        )}
      </div>
      {footer && <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-[13px] text-muted">{footer}</div>}
    </div>
  )
}

export function Pagination({ offset, limit, total, onChange, noun = 'results' }: { offset: number; limit: number; total: number; onChange: (offset: number) => void; noun?: string }) {
  const first = total === 0 ? 0 : offset + 1
  const last = Math.min(offset + limit, total)
  return (
    <>
      <span>Showing {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()} {noun}</span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - limit))}
          className="h-8 rounded-lg border border-line bg-surface px-3 text-[13px] text-ink disabled:text-muted disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={last >= total}
          onClick={() => onChange(offset + limit)}
          className="h-8 rounded-lg border border-line-strong bg-surface px-3 text-[13px] font-semibold text-ink disabled:font-normal disabled:text-muted disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus"
        >
          Next
        </button>
      </div>
    </>
  )
}
